// Session and capabilities.
//
// The server enforces every capability at the point of action. These are used
// only to avoid showing somebody a button that would refuse them — hiding a
// control is a courtesy, the refusal is the real thing.

import { createContext, useContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, onSignedOut } from './api';
import type { Meta, SessionUser } from './types';

interface SessionValue {
  user: SessionUser | null;
  caps: Set<string>;
  meta: Meta | null;
  fy: string | null;
  setFy: (fy: string) => void;
  loading: boolean;
  mustChangePassword: boolean;
  can: (cap: string) => boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [caps, setCaps] = useState<Set<string>>(new Set());
  const [meta, setMeta] = useState<Meta | null>(null);
  const [fy, setFyState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<{ user: SessionUser | null; caps?: string[] }>('/api/auth/me');
      if (!me.user) { setUser(null); setCaps(new Set()); setMeta(null); return; }
      setUser(me.user);
      setCaps(new Set(me.caps ?? []));
      if (me.user.must_change_pw) return;             // meta is gated until then
      const m = await api.get<Meta>('/api/meta');
      setMeta(m);
      setFyState((cur) => cur ?? localStorage.getItem('cocoma.fy') ?? m.fys[0] ?? null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    const off = onSignedOut(() => { setUser(null); setCaps(new Set()); setMeta(null); });
    return () => { off(); };
  }, []);

  const setFy = useCallback((next: string) => {
    setFyState(next);
    localStorage.setItem('cocoma.fy', next);
  }, []);

  const signOut = useCallback(async () => {
    await api.post('/api/auth/logout').catch(() => {});
    setUser(null); setCaps(new Set()); setMeta(null);
  }, []);

  const value = useMemo<SessionValue>(() => ({
    user, caps, meta, fy, setFy, loading,
    mustChangePassword: Boolean(user?.must_change_pw),
    can: (cap: string) => caps.has(cap),
    refresh, signOut,
  }), [user, caps, meta, fy, setFy, loading, refresh, signOut]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession must be used inside SessionProvider');
  return v;
}

/** Load data for a screen, with loading and error handled once. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    fn().then((d) => { if (live) setData(d); })
        .catch((e: Error) => { if (live) setError(e.message); })
        .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}
